import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { ReactComponent as SpinnerSvg } from "../../images/spinner.svg";
import { formTitle, meta } from "../../styles/typography";
import { transparentize } from "polished";
import { colours } from "../../styles/colours";
import {
	inputMarginBottomMobile,
	inputMarginBottomTablet,
	inputMarginBottom1200,
	inputMarginBottom1800,
	Loading,
	Spinner,
} from "./FormElements";
import { formApiBaseUrl } from "../../util/baseUrls";
import useResizeObserver from "use-resize-observer";
import { HubspotForm } from "./HubspotForm";

const Title = styled.h2`
	${formTitle};

	max-width: 554px;
	margin-bottom: 16px;

	@media screen and (min-width: 768px) {
		margin-bottom: 18px;
	}

	@media screen and (min-width: 1200px) {
		margin-bottom: 32px;
	}

	@media screen and (min-width: 1800px) {
		margin-bottom: 56px;
	}
`;
const FormWrapper = styled.div`
	width: 100%;
`;
const FormDetailsContainer = styled.div`
	@media screen and (min-width: 768px) {
		max-width: 480px;
	}
	@media screen and (min-width: 1200px) {
		max-width: 560px;
	}
	@media screen and (min-width: 1800px) {
		max-width: 560px;
	}
`;
const Terms = styled.div`
	${meta};
	color: ${transparentize(0.6, "white")};
	margin-top: ${inputMarginBottomMobile};
	width: 100%;

	@media screen and (min-width: 768px) {
		margin-top: ${inputMarginBottomTablet};
	}

	@media screen and (min-width: 1200px) {
		margin-top: ${inputMarginBottom1200};
	}

	@media screen and (min-width: 1800px) {
		margin-top: ${inputMarginBottom1800};
	}
	a {
		text-decoration: underline;
		transition: color 0.2s ease;
		&:visited {
			color: ${transparentize(0.6, "white")};
		}

		&:hover {
			color: ${colours.highlight};
		}
	}
`;
const Message = styled.div`
	height: ${(props) => props.innerHeight};
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
`;
const MessageText = styled.div`
	${meta};
	font-weight: 500;
	line-height: 1.2;
`;

const Newsletter = () => {
	const formId = "385f245c-4854-4223-b110-0c2bf0f03e77";
	const [formData, setFormData] = useState(null);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);

	const onSubmit = () => {
		setShowSuccessMessage(true);
	};

	const innerHeight = useRef(0);
	const { ref: formDetailsContainerEl, height: formDetailsContainerHeight } =
		useResizeObserver();

	useEffect(() => {
		if (!showSuccessMessage)
			innerHeight.current = formDetailsContainerHeight + "px";
	}, [formDetailsContainerHeight, showSuccessMessage]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const res = await fetch(
					`${formApiBaseUrl}/api/hubspot-form/${formId}/fields`
				);
				if (res.ok) {
					const formData = await res.json();
					setFormData(formData);
				} else {
					console.error("Error fetching form data", res.statusText);
				}
			} catch (error) {
				console.error("Error fetching form data", error);
			}
		};
		fetchData();
	}, [formId]);

	return (
		<FormWrapper>
			<Title>
				SIGN UP FOR THE <strong>LATEST NEWS</strong> AND{" "}
				<strong>INSIGHTS</strong>
			</Title>
			{showSuccessMessage && (
				<Message innerHeight={innerHeight.current}>
					<MessageText>
						Thank you! Please check your email to confirm your subscription.
					</MessageText>
				</Message>
			)}
			{!formData && (
				<Message>
					<Loading>
						<Spinner>
							<SpinnerSvg />
						</Spinner>
					</Loading>
				</Message>
			)}
			<FormDetailsContainer ref={formDetailsContainerEl}>
				{!showSuccessMessage && formData && (
					<>
						<HubspotForm formData={formData} onSubmit={onSubmit} />
						<Terms>
							<p>
								Your e-mail address is used to send you newsletters and
								marketing information on L’Atelier. You can unsubscribe at any
								time by using the unsubscribe link in our emails. More
								information about the management of your personal data and your
								rights can be found in our{" "}
								<a
									href="https://atelier.net/data-protection-notice"
									target="_blank"
									rel="noreferrer">
									Data Protection Notice
								</a>
								.
							</p>
						</Terms>
					</>
				)}
			</FormDetailsContainer>
		</FormWrapper>
	);
};

export default Newsletter;
